// src/components/StudentDiaryView.jsx
// Shown on the student's own diary history page (or embedded in admin view)

import { useState, useEffect } from "react";
import { subscribeDiary } from "../firebase/database";
import { formatDate } from "../utils/helpers";

const C = {
  bg: "#F7F8FC", card: "#FFFFFF", border: "#E4E7F0",
  accent: "#3D7EFF", accentSoft: "#EBF1FF",
  purple: "#7C3AED", purpleSoft: "#F5F3FF",
  text: "#111827", muted: "#6B7280", dim: "#E5E7EB",
};

export default function StudentDiaryView({ studentId, studentName }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    const unsub = subscribeDiary(studentId, (data) => {
      setEntries(data);
      setLoading(false);
    });
    return unsub;
  }, [studentId]);

  const filtered = entries.filter(e =>
    !search ||
    e.topics?.toLowerCase().includes(search.toLowerCase()) ||
    e.notes?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div style={{ padding: 24, color: C.muted, fontSize: 14, fontFamily: "DM Sans" }}>Loading diary…</div>;

  return (
    <div style={{ fontFamily: "DM Sans, sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: C.text }}>{studentName ? `${studentName}'s Diary` : "My Diary"}</div>
          <div style={{ fontSize: 13, color: C.muted }}>{entries.length} entries total</div>
        </div>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search entries…"
          style={{ background: "#F9FAFB", border: `1px solid ${C.border}`, borderRadius: 8, padding: "7px 12px", fontSize: 13, color: C.text, outline: "none", fontFamily: "DM Sans", width: 200 }}
        />
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 48, color: C.muted }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📔</div>
          <div style={{ fontSize: 14 }}>{entries.length === 0 ? "No diary entries yet." : "No entries match your search."}</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map((e, i) => (
            <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, borderLeft: `4px solid ${C.accent}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.accent }}>{formatDate(e.date)}</div>
                {i === 0 && (
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: C.accentSoft, color: C.accent, fontWeight: 600 }}>Latest</span>
                )}
              </div>
              {e.topics && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: C.muted, display: "inline-block", marginBottom: 3 }}>📚 Topics covered</span>
                  <div style={{ fontSize: 14, color: C.text, fontWeight: 500 }}>{e.topics}</div>
                </div>
              )}
              {e.notes && (
                <div>
                  <span style={{ fontSize: 12, color: C.muted, display: "inline-block", marginBottom: 3 }}>✏️ Notes</span>
                  <div style={{ fontSize: 14, color: C.text, lineHeight: 1.7, background: "#F9FAFB", padding: "10px 14px", borderRadius: 8 }}>{e.notes}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
